/**
 * AgendaModal — full-screen modal that displays the Iqra-generated meeting
 * agenda right after a multi-party Shura is created (Phase 4.1).
 *
 * The agenda is structured markdown:
 *   ## Objective
 *   ## Agenda (numbered)
 *   ## Suggested Outcomes (bullets)
 *
 * The modal lets the user:
 *   • Read the structured agenda
 *   • Copy it to clipboard (handy for sending out as a meeting brief)
 *   • Regenerate via /shura/multi-party-meeting/{id}/regenerate-agenda
 *
 * Pure presentational — the parent owns `open`, `meeting`, and the
 * regenerate / close handlers.
 */
import React, { useState } from 'react';
import { X, Copy, RefreshCw, Sparkles, Check } from 'lucide-react';

export function AgendaModal({ open, meeting, isDark, onClose, onRegenerate }) {
  const [copied, setCopied] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  if (!open || !meeting) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(meeting.agenda_md || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* ignore — clipboard may be blocked in some browsers */
    }
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    try { await onRegenerate(); }
    finally { setRegenerating(false); }
  };

  const overlayCls = isDark ? 'bg-black/75' : 'bg-slate-900/40';
  const accent = isDark ? '#AE52EA' : '#5E1F8B';

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-6 backdrop-blur-sm ${overlayCls}`}
      data-testid="shura-agenda-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl max-h-[85vh] overflow-y-auto pal-card pal-grid-bg"
        onClick={(e) => e.stopPropagation()}
        style={{
          boxShadow: isDark
            ? '0 40px 100px -40px rgba(174,82,234,0.55), inset 0 1px 0 rgba(255,255,255,0.04)'
            : '0 30px 60px -20px rgba(94,31,139,0.30)',
        }}
      >
        {/* Header — bracketed brief */}
        <div className="flex items-start justify-between p-6 border-b border-[rgba(174,82,234,0.18)]">
          <div className="flex items-start gap-3 min-w-0">
            <div
              className="h-10 w-10 rounded-md flex items-center justify-center shrink-0 border"
              style={{ backgroundColor: `${accent}1a`, color: accent, borderColor: `${accent}55` }}
            >
              <Sparkles size={18} />
            </div>
            <div className="min-w-0">
              <div className="pal-bracket">
                [IQRA · SHURA AGENDA]
              </div>
              <h2 className={`display text-xl mt-1.5 truncate ${isDark ? 'text-white' : 'text-[#0A0A0A]'}`}>
                {meeting.title || 'Multi-party Shura'}
              </h2>
              <div className="pal-mono text-[10px] opacity-65 mt-1.5">
                {String(meeting.participants?.length || 0).padStart(2, '0')} · PARTICIPANTS
                <span className="mx-2 opacity-40">·</span>
                {new Date(meeting.created_at).toLocaleString()}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="shura-agenda-close"
            className={`p-1.5 rounded ${isDark ? 'hover:bg-white/5 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}
          >
            <X size={18} />
          </button>
        </div>

        {/* Participants chips — bracket-style monospace IDs */}
        <div className="px-6 pt-4 flex flex-wrap gap-1.5 border-b pb-4 border-[rgba(174,82,234,0.12)]">
          {(meeting.participants || []).map((p, i) => (
            <span
              key={p.id}
              className={`pal-mono text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded border ${
                isDark ? 'border-[rgba(174,82,234,0.30)] text-slate-300 bg-[rgba(174,82,234,0.06)]' : 'border-slate-200 text-slate-600 bg-slate-50'
              }`}
            >
              <span className="opacity-50 mr-1">[{String(i + 1).padStart(2, '0')}]</span>
              {p.label}
            </span>
          ))}
        </div>

        {/* Agenda body — bracket-numbered intelligence brief */}
        <div className="p-6">
          <AgendaMarkdown md={meeting.agenda_md} isDark={isDark} accent={accent} />
        </div>

        {/* Footer actions */}
        <div className={`flex items-center justify-end gap-2 p-4 border-t border-[rgba(174,82,234,0.18)] ${
          isDark ? 'bg-[rgba(10,5,16,0.6)]' : 'bg-slate-50'
        }`}>
          <button
            onClick={handleCopy}
            data-testid="shura-agenda-copy"
            className={`px-3 py-2 text-[10px] uppercase tracking-[0.20em] rounded border transition-colors flex items-center gap-1.5 pal-mono ${
              isDark
                ? 'border-white/10 text-slate-200 hover:bg-white/5'
                : 'border-slate-200 text-slate-700 bg-white hover:bg-slate-100'
            }`}
          >
            {copied ? <Check size={12} /> : <Copy size={12} />}
            {copied ? 'COPIED' : 'COPY · BRIEF'}
          </button>
          <button
            onClick={handleRegenerate}
            disabled={regenerating}
            data-testid="shura-agenda-regenerate"
            className={`px-3 py-2 text-[10px] uppercase tracking-[0.20em] rounded border transition-colors flex items-center gap-1.5 disabled:opacity-50 pal-mono ${
              isDark
                ? 'border-[rgba(174,82,234,0.40)] text-[var(--ve-violet-bright)] hover:bg-[rgba(174,82,234,0.10)]'
                : 'border-[rgba(94,31,139,0.40)] text-[var(--ve-violet-shadow)] hover:bg-[rgba(94,31,139,0.06)]'
            }`}
          >
            <RefreshCw size={12} className={regenerating ? 'animate-spin' : ''} />
            {regenerating ? 'IQRA · PROCESSING' : 'REGENERATE'}
          </button>
          <button
            onClick={onClose}
            data-testid="shura-agenda-done"
            className={`px-4 py-2 text-[10px] uppercase tracking-[0.22em] rounded font-bold pal-mono transition-transform hover:translate-y-[-1px] ${
              isDark ? 'bg-[var(--ve-violet)] text-black hover:bg-[var(--ve-violet-bright)]'
                     : 'bg-[var(--ve-violet-shadow)] text-white hover:bg-[var(--ve-violet)]'
            }`}
          >
            DONE →
          </button>
        </div>
      </div>
    </div>
  );
}

/** Bracketed agenda renderer — bullet/numbered/h2 from markdown. */
function AgendaMarkdown({ md, isDark, accent }) {
  if (!md) {
    return <div className={`text-sm italic ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>No agenda generated.</div>;
  }
  const lines = md.split('\n');
  const blocks = [];
  let buf = { kind: null, items: [] };
  const flush = () => {
    if (buf.kind === 'numbered' || buf.kind === 'bullet') blocks.push({ ...buf });
    buf = { kind: null, items: [] };
  };
  lines.forEach((raw) => {
    const line = raw.trimEnd();
    if (!line) { flush(); return; }
    if (line.startsWith('## ')) {
      flush();
      blocks.push({ kind: 'h2', text: line.slice(3).trim() });
    } else if (/^\d+[.)]\s+/.test(line)) {
      if (buf.kind !== 'numbered') flush();
      buf.kind = 'numbered';
      buf.items.push(line.replace(/^\d+[.)]\s+/, ''));
    } else if (/^[-•]\s+/.test(line)) {
      if (buf.kind !== 'bullet') flush();
      buf.kind = 'bullet';
      buf.items.push(line.replace(/^[-•]\s+/, ''));
    } else {
      flush();
      blocks.push({ kind: 'p', text: line });
    }
  });
  flush();

  const subCls = isDark ? 'text-slate-300' : 'text-slate-700';
  return (
    <div className="space-y-5">
      {blocks.map((b, i) => {
        if (b.kind === 'h2') {
          return (
            <div key={i} className="flex items-center gap-3 mt-5 first:mt-0">
              <span className="h-px flex-1" style={{ background: `${accent}40` }} />
              <h3 className="pal-bracket" style={{ color: accent }}>
                [{b.text.toUpperCase()}]
              </h3>
              <span className="h-px flex-1" style={{ background: `${accent}40` }} />
            </div>
          );
        }
        if (b.kind === 'numbered') {
          return (
            <ol key={i} className="space-y-2.5">
              {b.items.map((it, j) => (
                <li key={j} className={`flex gap-3 text-sm leading-relaxed ${subCls}`}>
                  <span
                    className="flex-none pal-mono text-[10px] font-bold tracking-[0.10em] pt-1 self-start"
                    style={{ color: accent }}
                  >
                    [{String(j + 1).padStart(2, '0')}]
                  </span>
                  <span className="flex-1">{it}</span>
                </li>
              ))}
            </ol>
          );
        }
        if (b.kind === 'bullet') {
          return (
            <ul key={i} className="space-y-1.5">
              {b.items.map((it, j) => (
                <li key={j} className={`flex gap-2.5 text-sm leading-relaxed ${subCls}`}>
                  <span className="mt-1.5 h-1.5 w-1.5 flex-none" style={{ backgroundColor: accent, boxShadow: `0 0 6px ${accent}` }} />
                  <span className="flex-1">{it}</span>
                </li>
              ))}
            </ul>
          );
        }
        return (
          <p key={i} className={`text-sm leading-relaxed ${subCls}`}>{b.text}</p>
        );
      })}
    </div>
  );
}
